<template>
	<div class="period-filter-control">
		<RangeDatePickerControl v-model="range" :label="label" :presets="presets" :min-date="minDate" :max-date="maxDate" />
	</div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import RangeDatePickerControl from '@/components/controls/RangeDatePickerControl.vue';

const props = defineProps({
	label: { type: String, default: '' },
	presets: { type: Array, default: () => [] },
	required: { type: Boolean, default: false },
	preSelected: { type: Array, default: null },
	minDate: { type: [Date, String, Number], default: undefined },
	maxDate: { type: [Date, String, Number], default: undefined },
});
const emit = defineEmits(['change']);

const range = ref(Array.isArray(props.preSelected) ? [...props.preSelected] : []);
const defaultRange = computed(() => (Array.isArray(props.preSelected) ? props.preSelected : []));

watch(
	() => props.preSelected,
	(value) => {
		if (Array.isArray(value) && !isSameRange(value, range.value)) range.value = [...value];
	}
);

watch(range, () => emit('change', bounds()));

function dayValue(date) {
	if (!date) return null;
	const d = new Date(date);
	d.setHours(0, 0, 0, 0);
	return d.getTime();
}

function isSameRange(a, b) {
	const first = (list) => dayValue(list?.[0]);
	const last = (list) => dayValue(list?.[list.length - 1]);
	return first(a) === first(b) && last(a) === last(b);
}

function bounds() {
	const list = range.value ?? [];
	if (list.length === 0) return null;
	return { startDate: new Date(list[0]), endDate: new Date(list[list.length - 1]) };
}

defineExpose({
	getValues() {
		const value = bounds();
		return value ? [value.startDate, value.endDate] : null;
	},
	getFullValues() {
		return bounds();
	},
	isDirty() {
		return !isSameRange(range.value, defaultRange.value);
	},
	isValid() {
		if (!props.required) return true;
		return (range.value?.length ?? 0) >= 1;
	},
	isRequired() {
		return props.required;
	},
	selectItems(items) {
		range.value = Array.isArray(items) ? [...items] : [];
	},
	reset() {
		range.value = [...defaultRange.value];
	},
});
</script>

<style scoped>
.period-filter-control {
	min-width: 0;
}
</style>
